import { memo } from 'react';
import type { NotesPopoverNoteLike } from './ProjectNotesPopover.types';
import { DeleteIcon } from '@/components/ui/Icons';
import { IconButton } from '@/components/ui/IconButton/IconButton';
import { AutoResizeTextarea, NOTE_CONTENT_MAX_LENGTH } from '@/components/ui/AutoResizeTextarea/AutoResizeTextarea';
import { Badge } from '@/components/ui/Badge/Badge';
import { isRecentlyAdded, isTempNote } from '@/utils/projectNotes';
import { formatNoteDate } from './projectNotesPopoverUtils';

export const ProjectNotesPopoverNoteItem = memo(function ProjectNotesPopoverNoteItem({
  note,
  content,
  onContentChange,
  onDelete,
  isDeleting,
  deleteDisabled,
  draftDateHint,
}: {
  note: NotesPopoverNoteLike;
  content: string;
  onContentChange: (id: string, value: string) => void;
  onDelete: (id: string) => void;
  isDeleting: boolean;
  deleteDisabled: boolean;
  /** Подпись для temp-заметки вместо стандартной (например черновик в модалке создания проекта) */
  draftDateHint?: string;
}) {
  const isTemp = isTempNote(note.note_id);
  const isNew = !isTemp && isRecentlyAdded(note.created_at);

  return (
    <li className="project-notes-popover__item">
      <div className="project-notes-popover__textarea-wrap">
        <AutoResizeTextarea
          value={content}
          onChange={(e) => onContentChange(note.note_id, e.target.value)}
          placeholder="Текст заметки..."
          className="project-notes-popover__textarea project-notes-popover__textarea_inline"
          maxLength={NOTE_CONTENT_MAX_LENGTH}
          rows={1}
        />
      </div>
      <div className="project-notes-popover__item-footer">
        <div className="project-notes-popover__item-meta">
          <span className="project-notes-popover__item-date">
            {isTemp ? (draftDateHint ?? 'Будет добавлено после сохранения') : formatNoteDate(note.created_at)}
          </span>
          {!isTemp && note.created_by_name && (
            <>
              <span className="project-notes-popover__item-sep" aria-hidden="true" />
              <span className="project-notes-popover__item-author">{note.created_by_name}</span>
            </>
          )}
          {isNew && (
            <Badge
              variant="custom"
              bg="color-mix(in srgb, var(--accent-success) 15%, transparent)"
              color="var(--accent-success)"
            >
              Новая
            </Badge>
          )}
          {isTemp && (
            <Badge
              variant="custom"
              bg="color-mix(in srgb, var(--accent-warning) 15%, transparent)"
              color="var(--accent-warning)"
            >
              Черновик
            </Badge>
          )}
        </div>
        <div className="project-notes-popover__item-actions">
          <span className="project-notes-popover__item-char-count" aria-hidden="true">
            {content.length}/{NOTE_CONTENT_MAX_LENGTH}
          </span>
          <IconButton
            aria-label="Удалить"
            size="s"
            variant="danger"
            onClick={() => onDelete(note.note_id)}
            disabled={isDeleting || deleteDisabled}
          >
            <DeleteIcon />
          </IconButton>
        </div>
      </div>
    </li>
  );
});
